/**
 * PB-FND-75 — Long text field plugin (registry adapter).
 * Extracted from pb-field-runtime; wired via createLongTextAdapter(deps).
 */

import { buildAudioMicSvg } from '@theme/pb-field-audio';

function longTextUsesRecorder(settingsStr) {
  const s = String(settingsStr || '');
  return /recorder:\s*true/i.test(s) || /recorder:\s*on/i.test(s);
}

export function buildLongTextFieldHtml(pb, opts) {
  const label = opts.label || '';
  const settings = opts.settings || '';
  const parseSettings =
    pb && typeof pb.parseSettings === 'function' ? pb.parseSettings.bind(pb) : function () {
      return {};
    };
  const s = parseSettings(settings);
  const existingVal = opts.existingVal != null ? opts.existingVal : '';
  const existingAudio = opts.existingAudio || '';
  const esc =
    pb && typeof pb.escapeAttr === 'function'
      ? pb.escapeAttr.bind(pb)
      : function (v) {
          return String(v ?? '');
        };
  const audioPropertyLabel =
    pb && typeof pb.audioPropertyLabel === 'function'
      ? pb.audioPropertyLabel.bind(pb)
      : function (textLabel) {
          return String(textLabel || '').trim() + ' (audio)';
        };
  const attrs =
    'class="builder-input pb-long-text' +
    (opts.extraClass ? ' ' + opts.extraClass : '') +
    '" rows="3" oninput="window.productBuilder.validateText(this)" data-label="' +
    esc(label) +
    '" data-settings="' +
    esc(settings) +
    '" placeholder="Type here..."';
  if (!s.recorder) {
    return '<textarea ' + attrs + '>' + esc(existingVal) + '</textarea>';
  }
  const maxSec = s.recorder_max || 300;
  const audioLabel = audioPropertyLabel(label);
  const hasAudio = !!existingAudio;
  const reqAttr = s.required ? 'true' : 'false';
  const micSvg = buildAudioMicSvg({ width: 20, height: 20, className: '' });
  const playSvg =
    '<svg class="pb-lt-icon-play" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  const pauseSvg =
    '<svg class="pb-lt-icon-pause" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" hidden><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
  const stopSvg =
    '<span class="pb-lt-rec-dot" aria-hidden="true"></span><span class="pb-lt-btn-stop-label">Stop</span>';
  const trashSvg =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>';
  return (
    '<div class="pb-longtext-wrap" data-recorder-max="' +
    maxSec +
    '" data-text-label="' +
    esc(label) +
    '">' +
    '<div class="pb-longtext-box">' +
    '<textarea ' +
    attrs +
    '>' +
    esc(existingVal) +
    '</textarea>' +
    '<div class="pb-lt-idle"' +
    (hasAudio ? ' hidden' : '') +
    '><button type="button" class="pb-lt-btn-mic" aria-label="Record voice note">' +
    micSvg +
    '</button></div></div>' +
    '<div class="pb-lt-recording" hidden><div class="pb-lt-rec-strip"><canvas class="pb-lt-wave-canvas" height="32"></canvas>' +
    '<button type="button" class="pb-lt-btn-stop">' +
    stopSvg +
    '</button>' +
    '<button type="button" class="pb-lt-btn-cancel" aria-label="Cancel recording">×</button></div></div>' +
    '<div class="pb-lt-playback"' +
    (hasAudio ? '' : ' hidden') +
    '><div class="pb-lt-playback-bar"><button type="button" class="pb-lt-btn-play" aria-label="Play">' +
    playSvg +
    pauseSvg +
    '</button>' +
    '<canvas class="pb-lt-wave-canvas pb-lt-wave-playback" height="32"></canvas></div>' +
    '<button type="button" class="pb-lt-btn-delete" aria-label="Delete recording">' +
    trashSvg +
    '</button></div>' +
    '<input type="hidden" class="pb-lt-audio-input" data-label="' +
    esc(audioLabel) +
    '" data-required="' +
    reqAttr +
    '" data-valid="' +
    (hasAudio ? 'true' : 'false') +
    '" value="' +
    esc(existingAudio) +
    '">' +
    '<div class="pb-lt-message" style="display:none;"></div></div>'
  );
}

/**
 * @param {object} deps — shared field-runtime helpers
 */
export function createLongTextAdapter(deps) {
  const {
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    renderControlShell,
    renderImageColumn,
    resolveAdapterSettings,
    validateTextInput,
  } = deps;

  return {
    id: 'long-text',
    matches(field) {
      const t = fieldTypeLower(field);
      return t.includes('long text') || t.includes('longtext');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsStr = settingsToString(field.settings);
      const existingVal = resolveSelections(ctx)[field.label] || '';
      const pb = typeof window !== 'undefined' ? window.productBuilder : null;
      let inputHtml;
      if (pb) {
        const audioLabel =
          typeof pb.audioPropertyLabel === 'function'
            ? pb.audioPropertyLabel(field.label)
            : String(field.label || '').trim() + ' (audio)';
        inputHtml = buildLongTextFieldHtml(pb, {
          label: field.label,
          settings: settingsStr,
          existingVal,
          existingAudio: resolveSelections(ctx)[audioLabel] || '',
        });
      } else if (longTextUsesRecorder(settingsStr)) {
        inputHtml =
          '<div class="pb-longtext-recorder-shell" data-label="' +
          esc(field.label) +
          '" data-settings="' +
          esc(settingsStr) +
          '"></div>';
      } else {
        inputHtml =
          '<textarea class="builder-input pb-long-text" rows="3" oninput="window.productBuilder.validateText(this)" data-label="' +
          esc(field.label) +
          '" data-settings="' +
          esc(settingsStr) +
          '" placeholder="Type here...">' +
          esc(existingVal) +
          '</textarea>';
      }
      const visRaw = String(field.visual || '').trim();
      return {
        controlHtml: renderControlShell(field, inputHtml, ctx, { useQuestionHighlight: !visRaw }),
        imageHtml: renderImageColumn(field, ctx),
        stepAttrs: '',
        extraClasses: !visRaw ? 'pb-step-hide-question' : '',
      };
    },
    initStep() {},
    validateStep(stepEl, ctx) {
      const resolved = resolveAdapterSettings(
        stepEl,
        ctx,
        '.pb-long-text[data-label], textarea.builder-input[data-label]',
        'textarea.builder-input[data-label]'
      );
      if (!resolved) return true;
      return validateTextInput(resolved.el, resolved.settings);
    },
    hydrateStep(stepEl, ctx) {
      const field = ctx.field;
      const val = resolveSelections(ctx)[field.label] || '';
      const input =
        stepEl && stepEl.querySelector('.pb-long-text[data-label], textarea.builder-input[data-label]');
      if (input) input.value = val;
    },
    flushStep(stepEl, ctx) {
      const field = ctx.field;
      const input =
        stepEl && stepEl.querySelector('.pb-long-text[data-label], textarea.builder-input[data-label]');
      if (!input || !ctx.selections) return;
      ctx.selections[field.label] = input.value;
    },
  };
}
